import { create } from 'zustand';
import { supabase, DBWarehouse, DBStorageArea } from '../lib/supabase';
import { getActiveCollectionId } from './collectionStore';

const LEGACY_STORAGE_KEY = 'netprobuilder_warehouses';

export interface Warehouse {
  id: string;
  name: string;
  isDefault: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface StorageArea {
  id: string;
  warehouseId: string;
  name: string;
  isDefault: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

interface WarehouseState {
  warehouses: Warehouse[];
  activeWarehouseId: string | null;
  defaultWarehouseId: string | null;
  storageAreasByWarehouse: Record<string, StorageArea[]>;
  activeStorageAreaIdByWarehouse: Record<string, string | null>;
  defaultStorageAreaIdByWarehouse: Record<string, string | null>;
  isLoading: boolean;
  error: string | null;

  // Lifecycle
  initialize: () => Promise<void>;

  // Warehouses
  addWarehouse: (name: string) => Promise<Warehouse | null>;
  removeWarehouse: (id: string) => Promise<void>;
  renameWarehouse: (id: string, name: string) => Promise<void>;
  reorderWarehouses: (fromIndex: number, toIndex: number) => Promise<void>;
  setActiveWarehouse: (id: string) => void;
  setDefaultWarehouse: (id: string) => Promise<void>;

  // Storage areas
  addStorageArea: (warehouseId: string, name: string) => Promise<StorageArea | null>;
  removeStorageArea: (warehouseId: string, areaId: string) => Promise<void>;
  renameStorageArea: (warehouseId: string, areaId: string, name: string) => Promise<void>;
  reorderStorageAreas: (warehouseId: string, fromIndex: number, toIndex: number) => Promise<void>;
  setActiveStorageArea: (warehouseId: string, areaId: string) => void;
  setDefaultStorageArea: (warehouseId: string, areaId: string) => Promise<void>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const toWarehouse = (row: DBWarehouse): Warehouse => ({
  id: row.id,
  name: row.name,
  isDefault: row.is_default,
  sortOrder: row.sort_order,
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
});

const toStorageArea = (row: DBStorageArea): StorageArea => ({
  id: row.id,
  warehouseId: row.warehouse_id,
  name: row.name,
  isDefault: row.is_default,
  sortOrder: row.sort_order,
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
});

// Build derived maps from flat arrays
const buildMaps = (
  warehouses: Warehouse[],
  areas: StorageArea[]
): {
  storageAreasByWarehouse: Record<string, StorageArea[]>;
  defaultStorageAreaIdByWarehouse: Record<string, string | null>;
} => {
  const storageAreasByWarehouse: Record<string, StorageArea[]> = {};
  const defaultStorageAreaIdByWarehouse: Record<string, string | null> = {};

  for (const wh of warehouses) {
    const whAreas = areas
      .filter(a => a.warehouseId === wh.id)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    storageAreasByWarehouse[wh.id] = whAreas;
    const defaultArea = whAreas.find(a => a.isDefault) ?? whAreas[0] ?? null;
    defaultStorageAreaIdByWarehouse[wh.id] = defaultArea?.id ?? null;
  }

  return { storageAreasByWarehouse, defaultStorageAreaIdByWarehouse };
};

// One-time migration of legacy localStorage data into Supabase
const migrateLegacyData = async (collectionId: string) => {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw) as any;
    const legacyWarehouses = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed?.warehouses)
      ? parsed.warehouses
      : [];

    if (legacyWarehouses.length === 0) {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      return;
    }

    const legacyAreas: Record<string, any[]> = parsed?.storageAreasByWarehouse ?? {};
    const legacyDefault: string | null = parsed?.defaultWarehouseId ?? null;

    for (let wi = 0; wi < legacyWarehouses.length; wi++) {
      const lw = legacyWarehouses[wi];
      const { data: whRow } = await supabase
        .from('warehouses')
        .insert({
          name: lw.name,
          is_default: lw.id === legacyDefault,
          sort_order: wi,
          collection_id: collectionId,
        })
        .select()
        .single();

      if (!whRow) continue;

      const legacyAreasForWh: any[] = legacyAreas[lw.id] ?? [];
      const legacyDefaultAreaId: string | null =
        parsed?.defaultStorageAreaIdByWarehouse?.[lw.id] ?? null;

      for (let ai = 0; ai < legacyAreasForWh.length; ai++) {
        const la = legacyAreasForWh[ai];
        await supabase.from('storage_areas').insert({
          warehouse_id: whRow.id,
          name: la.name,
          is_default: la.id === legacyDefaultAreaId,
          sort_order: ai,
        });
      }
    }

    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (err) {
    console.error('Legacy warehouse migration failed:', err);
  }
};

// ─── Store ───────────────────────────────────────────────────────────────────

export const useWarehouseStore = create<WarehouseState>((set, get) => ({
  warehouses: [],
  activeWarehouseId: null,
  defaultWarehouseId: null,
  storageAreasByWarehouse: {},
  activeStorageAreaIdByWarehouse: {},
  defaultStorageAreaIdByWarehouse: {},
  isLoading: true,
  error: null,

  initialize: async () => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) {
      set({ warehouses: [], isLoading: false });
      return;
    }

    set({ isLoading: true, error: null });

    // Migrate any legacy localStorage data first
    await migrateLegacyData(collectionId);

    // Fetch warehouses
    const { data: whRows, error: whError } = await supabase
      .from('warehouses')
      .select('*')
      .eq('collection_id', collectionId)
      .order('sort_order', { ascending: true });

    if (whError) {
      console.error('Failed to load warehouses:', whError.message);
      set({ isLoading: false, error: whError.message });
      return;
    }

    // Fetch all storage areas
    const { data: areaRows, error: areaError } = await supabase
      .from('storage_areas')
      .select('*')
      .order('sort_order', { ascending: true });

    if (areaError) {
      console.error('Failed to load storage areas:', areaError.message);
      set({ isLoading: false, error: areaError.message });
      return;
    }

    const warehouses: Warehouse[] = (whRows ?? []).map(toWarehouse);
    const areas: StorageArea[] = (areaRows ?? []).map(toStorageArea);

    const { storageAreasByWarehouse, defaultStorageAreaIdByWarehouse } = buildMaps(warehouses, areas);

    const defaultWh = warehouses.find(w => w.isDefault) ?? warehouses[0] ?? null;
    const defaultWarehouseId = defaultWh?.id ?? null;

    const activeStorageAreaIdByWarehouse: Record<string, string | null> = {};
    for (const wh of warehouses) {
      activeStorageAreaIdByWarehouse[wh.id] =
        defaultStorageAreaIdByWarehouse[wh.id] ?? null;
    }

    set({
      warehouses,
      activeWarehouseId: defaultWarehouseId,
      defaultWarehouseId,
      storageAreasByWarehouse,
      activeStorageAreaIdByWarehouse,
      defaultStorageAreaIdByWarehouse,
      isLoading: false,
    });
  },

  // ── Warehouses ──

  addWarehouse: async (name: string) => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) return null;

    const trimmed = name.trim();
    if (!trimmed) return null;
    const state = get();
    const sortOrder = state.warehouses.length;
    const isFirstWarehouse = state.warehouses.length === 0;

    const { data, error } = await supabase
      .from('warehouses')
      .insert({
        name: trimmed,
        is_default: isFirstWarehouse,
        sort_order: sortOrder,
        collection_id: collectionId,
      })
      .select()
      .single();

    if (error || !data) {
      set({ error: error?.message ?? 'Failed to add warehouse' });
      return null;
    }
    const wh = toWarehouse(data as DBWarehouse);

    set(s => ({
      warehouses: [...s.warehouses, wh],
      activeWarehouseId: s.activeWarehouseId ?? wh.id,
      defaultWarehouseId: s.defaultWarehouseId ?? wh.id,
      storageAreasByWarehouse: { ...s.storageAreasByWarehouse, [wh.id]: [] },
      activeStorageAreaIdByWarehouse: { ...s.activeStorageAreaIdByWarehouse, [wh.id]: null },
      defaultStorageAreaIdByWarehouse: { ...s.defaultStorageAreaIdByWarehouse, [wh.id]: null },
      error: null,
    }));
    return wh;
  },

  removeWarehouse: async (id: string) => {
    await supabase.from('warehouses').delete().eq('id', id);

    set(s => {
      const warehouses = s.warehouses.filter(w => w.id !== id);
      const nextDefaultId = s.defaultWarehouseId === id ? (warehouses[0]?.id ?? null) : s.defaultWarehouseId;
      const nextActiveId = s.activeWarehouseId === id ? (warehouses[0]?.id ?? null) : s.activeWarehouseId;
      const storageAreasByWarehouse = { ...s.storageAreasByWarehouse };
      delete storageAreasByWarehouse[id];
      const activeStorageAreaIdByWarehouse = { ...s.activeStorageAreaIdByWarehouse };
      delete activeStorageAreaIdByWarehouse[id];
      const defaultStorageAreaIdByWarehouse = { ...s.defaultStorageAreaIdByWarehouse };
      delete defaultStorageAreaIdByWarehouse[id];
      return { warehouses, defaultWarehouseId: nextDefaultId, activeWarehouseId: nextActiveId, storageAreasByWarehouse, activeStorageAreaIdByWarehouse, defaultStorageAreaIdByWarehouse };
    });
  },

  renameWarehouse: async (id: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await supabase.from('warehouses').update({ name: trimmed }).eq('id', id);
    set(s => ({ warehouses: s.warehouses.map(w => w.id === id ? { ...w, name: trimmed, updatedAt: new Date() } : w) }));
  },

  reorderWarehouses: async (fromIndex: number, toIndex: number) => {
    const state = get();
    const next = [...state.warehouses];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    // Optimistic update
    set({ warehouses: next });

    // Persist new sort_order values
    await Promise.all(
      next.map((wh, i) => supabase.from('warehouses').update({ sort_order: i }).eq('id', wh.id))
    );
  },

  setActiveWarehouse: (id: string) => {
    const state = get();
    if (!state.warehouses.some(w => w.id === id)) return;
    set(s => ({
      activeWarehouseId: id,
      activeStorageAreaIdByWarehouse: {
        ...s.activeStorageAreaIdByWarehouse,
        [id]: s.defaultStorageAreaIdByWarehouse[id] ?? s.storageAreasByWarehouse[id]?.[0]?.id ?? null,
      },
    }));
  },

  setDefaultWarehouse: async (id: string) => {
    const state = get();
    const isAlreadyDefault = state.defaultWarehouseId === id;
    const newDefaultId = isAlreadyDefault ? null : id;

    // Optimistic UI — clear star on previous default immediately
    set(s => ({
      defaultWarehouseId: newDefaultId,
      activeWarehouseId: newDefaultId ?? s.activeWarehouseId,
      warehouses: s.warehouses.map(w => ({ ...w, isDefault: w.id === newDefaultId })),
    }));

    await supabase.from('warehouses').update({ is_default: false }).eq('is_default', true);
    if (newDefaultId) {
      await supabase.from('warehouses').update({ is_default: true }).eq('id', newDefaultId);
    }
  },

  // ── Storage Areas ──

  addStorageArea: async (warehouseId: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const state = get();
    const existing = state.storageAreasByWarehouse[warehouseId] ?? [];
    const isFirst = existing.length === 0;

    const { data, error } = await supabase
      .from('storage_areas')
      .insert({ warehouse_id: warehouseId, name: trimmed, is_default: isFirst, sort_order: existing.length })
      .select()
      .single();

    if (error || !data) {
      set({ error: error?.message ?? 'Failed to add storage area' });
      return null;
    }
    const area = toStorageArea(data as DBStorageArea);

    set(s => {
      const areas = [...(s.storageAreasByWarehouse[warehouseId] ?? []), area];
      return {
        storageAreasByWarehouse: { ...s.storageAreasByWarehouse, [warehouseId]: areas },
        defaultStorageAreaIdByWarehouse: {
          ...s.defaultStorageAreaIdByWarehouse,
          [warehouseId]: s.defaultStorageAreaIdByWarehouse[warehouseId] ?? area.id,
        },
        activeStorageAreaIdByWarehouse: { ...s.activeStorageAreaIdByWarehouse, [warehouseId]: area.id },
        activeWarehouseId: warehouseId,
        error: null,
      };
    });
    return area;
  },

  removeStorageArea: async (warehouseId: string, areaId: string) => {
    await supabase.from('storage_areas').delete().eq('id', areaId);

    set(s => {
      const areas = (s.storageAreasByWarehouse[warehouseId] ?? []).filter(a => a.id !== areaId);
      const wasDefault = s.defaultStorageAreaIdByWarehouse[warehouseId] === areaId;
      const nextDefault = wasDefault ? (areas[0]?.id ?? null) : (s.defaultStorageAreaIdByWarehouse[warehouseId] ?? null);
      const wasActive = s.activeStorageAreaIdByWarehouse[warehouseId] === areaId;
      const nextActive = wasActive ? (areas[0]?.id ?? null) : (s.activeStorageAreaIdByWarehouse[warehouseId] ?? null);
      return {
        storageAreasByWarehouse: { ...s.storageAreasByWarehouse, [warehouseId]: areas },
        defaultStorageAreaIdByWarehouse: { ...s.defaultStorageAreaIdByWarehouse, [warehouseId]: nextDefault },
        activeStorageAreaIdByWarehouse: { ...s.activeStorageAreaIdByWarehouse, [warehouseId]: nextActive },
      };
    });
  },

  renameStorageArea: async (warehouseId: string, areaId: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await supabase.from('storage_areas').update({ name: trimmed }).eq('id', areaId);
    set(s => {
      const areas = (s.storageAreasByWarehouse[warehouseId] ?? []).map(a =>
        a.id === areaId ? { ...a, name: trimmed, updatedAt: new Date() } : a
      );
      return { storageAreasByWarehouse: { ...s.storageAreasByWarehouse, [warehouseId]: areas } };
    });
  },

  reorderStorageAreas: async (warehouseId: string, fromIndex: number, toIndex: number) => {
    const state = get();
    const areas = [...(state.storageAreasByWarehouse[warehouseId] ?? [])];
    const [moved] = areas.splice(fromIndex, 1);
    areas.splice(toIndex, 0, moved);

    set(s => ({ storageAreasByWarehouse: { ...s.storageAreasByWarehouse, [warehouseId]: areas } }));

    await Promise.all(
      areas.map((a, i) => supabase.from('storage_areas').update({ sort_order: i }).eq('id', a.id))
    );
  },

  setActiveStorageArea: (warehouseId: string, areaId: string) => {
    set(s => ({
      activeStorageAreaIdByWarehouse: { ...s.activeStorageAreaIdByWarehouse, [warehouseId]: areaId },
    }));
  },

  setDefaultStorageArea: async (warehouseId: string, areaId: string) => {
    const state = get();
    const isAlreadyDefault = state.defaultStorageAreaIdByWarehouse[warehouseId] === areaId;
    const newDefaultId = isAlreadyDefault ? null : areaId;

    await supabase.from('storage_areas').update({ is_default: false }).eq('warehouse_id', warehouseId);
    if (newDefaultId) {
      await supabase.from('storage_areas').update({ is_default: true }).eq('id', newDefaultId);
    }

    set(s => ({
      defaultStorageAreaIdByWarehouse: { ...s.defaultStorageAreaIdByWarehouse, [warehouseId]: newDefaultId },
      storageAreasByWarehouse: {
        ...s.storageAreasByWarehouse,
        [warehouseId]: (s.storageAreasByWarehouse[warehouseId] ?? []).map(a => ({
          ...a,
          isDefault: a.id === newDefaultId,
        })),
      },
    }));
  },
}));
